import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ICampaignOverview } from '../../model/briefing/overview/ICampaignOverview';
import { IInviteInfluencer } from '../../model/book/Invites';
import { IInfluencerDistribution } from '../../app/influencer/offer/dto/IInfluencerDistribution';
import { WarningToast } from '../../utils/toasters';
import { IOfferItemsRequest } from '../../model/offer/IOfferItems';
import { User } from '../../model/user/User';
import { OfferStatusEnum } from '../../enum/OfferStatusEnum';
export interface IOfferAgency {
    selectedMatchedUser?: User;
    activeTabModal: number;
    campaignId: string;
    campaign?: ICampaignOverview;
    isDistributionChecked: boolean;
    isLegalChecked: boolean;
    isStepperFinished: boolean;
    isEditable?: boolean;
    canCounter?: boolean;
    offerModalStatus: string;
    initialOffer: IInviteInfluencer;
    additionalItemId: number;
    distributionArr: IInfluencerDistribution[];
    hasLegal: boolean;
    hasDistribution: boolean;
    requiredFieldChangeColor?: boolean;
    canWriteComment?: boolean;
    isCharity: boolean;
    hasUnreadComments: boolean;
    hasFee?: boolean;
    feePercentage?: number;
    isIvConnectedClub?: boolean;
    inviteAttachment?: Blob[] | MediaSource[];
}

const initialOfferState: IOfferAgency = {
    selectedMatchedUser: undefined,
    activeTabModal: 0,
    campaignId: '',
    canCounter: undefined,
    campaign: undefined,
    distributionArr: [],
    offerModalStatus: 'preview',
    isDistributionChecked: false,
    isLegalChecked: false,
    isStepperFinished: false,
    additionalItemId: 1,
    isEditable: undefined,
    initialOffer: {
        description: '',
        personalOffer: '',
        price: 0,
        offerItems: [],
        distribution: [],
    },
    hasLegal: true,
    hasDistribution: false,
    requiredFieldChangeColor: false,
    canWriteComment: false,
    isCharity: false,
    hasUnreadComments: false,
    hasFee: false,
    isIvConnectedClub: false,
    inviteAttachment: undefined,
};
export const offerAgency = createSlice({
    name: 'agencyOffer',
    initialState: initialOfferState,
    reducers: {
        setActiveTab: (state, action) => {
            const currentStep = action.payload;
            if (!state.isDistributionChecked && currentStep > 2) {
                WarningToast('Please check the required fields');
                return { ...state, activeTabModal: 2, requiredFieldChangeColor: true };
            } else if (!state.isLegalChecked && currentStep > 3 && state.hasLegal) {
                WarningToast('Please check the required fields');
                return { ...state, activeTabModal: 3, requiredFieldChangeColor: true };
            } else if (!state.isStepperFinished && currentStep > 3) {
                return {
                    ...state,
                    isStepperFinished: true,
                    activeTabModal: 4,
                };
            }
            if (currentStep === 5) {
                return {
                    ...state,
                    requiredFieldChangeColor: false,
                    activeTabModal: action.payload,
                    hasUnreadComments: false,
                };
            }
            return { ...state, requiredFieldChangeColor: false, activeTabModal: action.payload };
        },
        setOfferModalStatus: (state, action) => {
            return { ...state, offerModalStatus: action.payload };
        },
        setOfferModalDetails: (state, action) => {
            const campaign: ICampaignOverview = action.payload;
            const offerItems: IOfferItemsRequest[] = [];
            let additionalItems: IInfluencerDistribution[] = [];
            campaign.offerPlaceholder?.totalPlatforms?.map(platform =>
                platform.offerItems.map(item =>
                    offerItems.push({
                        mainPlatformId: platform.id,
                        socialPlatformId: item.socialPlatform.id,
                        nbPosts: item.nbPosts ?? 0,
                        price: item.price,
                        oldPrice: item.oldPrice,
                    })
                )
            );
            if (campaign?.offerPlaceholder?.additionalOfferItems?.length) {
                campaign?.offerPlaceholder?.additionalOfferItems.map(item => additionalItems.push(item));
            }
            if (!campaign.offers.length) {
                additionalItems = [...campaign?.offerPlaceholder?.distribution];
            }
            return {
                ...state,
                campaign: action.payload,
                activeTabModal: campaign?.offers?.length && 4,
                offerModalStatus: campaign?.offers?.length ? 'preview' : 'negotiate',
                distributionArr: campaign?.offerPlaceholder?.distribution,
                isStepperFinished: !!campaign?.offers?.length,
                isDistributionChecked: !!campaign?.offers?.length,
                isLegalChecked: !!campaign?.offers?.length,
                isEditable: campaign?.offerPlaceholder?.flags?.isEditable,
                canCounter: campaign?.offerPlaceholder?.flags?.canCounter,
                initialOffer: {
                    ...state.initialOffer,
                    offerItems: campaign?.offers[0]?.isAddonsOnly ? [] : offerItems,
                    distribution: additionalItems,
                    description: campaign?.offers[0]?.description ?? '',
                    personalOffer: campaign?.offers[0]?.personalOffer,
                    isAddonsOnly: campaign?.offers[0]?.isAddonsOnly,
                },
                // hasLegal: (!campaign.hasExclusivity && !campaign.hasByLaws) ? false : true,
                hasDistribution: !campaign.brandedContent && !campaign.usageRights ? false : true,
                canWriteComment: [OfferStatusEnum.NEGOTIATE, OfferStatusEnum.PENDING].includes(
                    campaign?.offers[0]?.status
                ),
                isCharity: campaign?.isCharity,
                hasUnreadComments: campaign?.offers[0]?.hasUnreadComments,
                hasFee: campaign?.offers[0]?.hasInfluencerFee ?? campaign?.offerPlaceholder?.user?.hasInfluencerFee,
                feePercentage:
                    campaign?.offers[0]?.influencerFeePercentage ??
                    campaign?.offerPlaceholder?.user?.influencerFeePercentage,
                isIvConnectedClub:
                    campaign?.offers[0]?.isIvConnectedClub ?? campaign?.offerPlaceholder?.user?.isIvConnectedClub,
            };
        },
        setInitialState: () => {
            return initialOfferState;
        },
        setSelectMatchedUser: (state, action) => {
            return { ...state, selectedMatchedUser: action.payload };
        },
        setDistributionItems: (state, action) => {
            const distributionArr = action.payload;
            return { ...state, distributionArr };
        },
        setDistributionCheck: (state, action) => {
            return { ...state, isDistributionChecked: action.payload };
        },
        setLegalCheck: (state, action) => {
            return { ...state, isLegalChecked: action.payload };
        },
        setFinishStepper: state => {
            return { ...state, isStepperFinished: true };
        },
        setOfferItems: (state, action) => {
            const offerItems = action.payload;
            return { ...state, initialOffer: { ...state.initialOffer, offerItems } };
        },
        setAdditionalItems: (state, action) => {
            const distribution = action.payload;
            return { ...state, initialOffer: { ...state.initialOffer, distribution } };
        },
        addAdditionalItem: (state, action) => {
            const distributionItem = action.payload;
            const distributionArr: IInfluencerDistribution[] = [...state?.initialOffer?.distribution];
            distributionArr.push({ ...distributionItem });
            if (distributionItem?.type === 'additional') {
                return {
                    ...state,
                    additionalItemId: state.additionalItemId + 1,
                    initialOffer: { ...state.initialOffer, distribution: distributionArr },
                };
            }
            return { ...state, initialOffer: { ...state.initialOffer, distribution: distributionArr } };
        },
        deleteAdditionItem: (state, action) => {
            const index = action.payload;
            const distributionArr = [...state?.initialOffer?.distribution];
            distributionArr.splice(index, 1);
            return { ...state, initialOffer: { ...state.initialOffer, distribution: distributionArr } };
        },
        setPersonalOfferDescription: (state, action) => {
            return { ...state, initialOffer: { ...state.initialOffer, personalOffer: action.payload } };
        },
        setDescription: (state, action) => {
            return {
                ...state,
                initialOffer: { ...state.initialOffer, description: action.payload },
                requiredFieldChangeColor: false,
            };
        },
        setCampaignId: (state, action) => {
            return { ...state, campaignId: action.payload };
        },
        setInviteAttachment: (
            state,
            { payload: inviteAttachment }: PayloadAction<Blob[] | MediaSource[] | undefined>
        ) => {
            return { ...state, inviteAttachment };
        },
    },
});

export const {
    setActiveTab,
    setOfferModalStatus,
    setOfferModalDetails,
    setInitialState,
    setLegalCheck,
    setDistributionCheck,
    setFinishStepper,
    setOfferItems,
    addAdditionalItem,
    deleteAdditionItem,
    setDistributionItems,
    setAdditionalItems,
    setDescription,
    setPersonalOfferDescription,
    setCampaignId,
    setSelectMatchedUser,
    setInviteAttachment,
} = offerAgency.actions;
